'use client'

import './editable-text.css'

import { useCallback } from 'react'
import { cn } from '@/lib/utils'
import { useNotebook } from '@/lib/notebook/core/store'
import { useActiveEditor } from '@/lib/notebook/core/active-editor-context'
import { NotebookStaticField } from '@/lib/notebook/editor/fields/NotebookStaticField'
import { NotebookTiptapField } from '@/lib/notebook/editor/fields/NotebookTiptapField'

/**
 * Lazy notebook field — static preview until focus; one live ProseMirror at a time.
 */
export function EditableText({
  instanceKey,
  value,
  children,
  onSave,
  debounceMs: _debounceMs = 400,
  className = '',
  style = {},
  multiline = true,
  linkify = false,
  richText,
  locked = false,
  inline = false,
  rowIndex = null,
  rowId = null,
  rowField = 'text',
  isolatePasteBubble = false,
  maxLength,
  onBackspaceWhenEmpty,
  onEditEnd,
  placeholder = '',
  bubbleToolbar = true,
}) {
  /* Preserve marks (color, bold) on single-line row fields; plain text only for inline chips */
  const useRichText =
    richText !== undefined ? richText : multiline || !inline
  const resolvedValue =
    value !== undefined
      ? value
      : typeof children === 'string'
        ? children
        : ''

  const singleLine = !multiline

  const resolvedInstanceKey =
    instanceKey ??
    (rowId ? `row:${rowId}:${rowField}` : `field:${resolvedValue.slice(0, 24)}`)

  const sessionEpoch = useNotebook((s) => s.sessionEpoch)
  const interactionLocked = useNotebook((s) => s.publicMode || s.locked)
  const effectiveLocked = locked || interactionLocked
  const { activate, deactivate, isActive } = useActiveEditor()
  const editorMountKey = `${resolvedInstanceKey}-${sessionEpoch}`
  const live = !effectiveLocked && isActive(resolvedInstanceKey)

  const handleActivate = useCallback(() => {
    if (effectiveLocked) return
    activate(resolvedInstanceKey)
  }, [activate, effectiveLocked, resolvedInstanceKey])

  const handleDeactivate = useCallback(() => {
    deactivate(resolvedInstanceKey)
    onEditEnd?.()
  }, [deactivate, onEditEnd, resolvedInstanceKey])

  if (!live) {
    return (
      <NotebookStaticField
        value={resolvedValue}
        richText={useRichText}
        singleLine={singleLine}
        inline={inline}
        locked={effectiveLocked}
        placeholder={placeholder}
        className={cn('editable-text', className)}
        style={style}
        onActivate={effectiveLocked ? undefined : handleActivate}
      />
    )
  }

  return (
    <NotebookTiptapField
      key={editorMountKey}
      instanceKey={resolvedInstanceKey}
      sessionEpoch={sessionEpoch}
      value={resolvedValue}
      onSave={onSave}
      locked={effectiveLocked}
      richText={useRichText}
      singleLine={singleLine}
      inline={inline}
      rowIndex={rowIndex}
      rowId={rowId}
      rowField={rowField}
      isolatePasteBubble={isolatePasteBubble}
      maxLength={maxLength}
      onBackspaceWhenEmpty={onBackspaceWhenEmpty}
      autoFocus
      onDeactivate={handleDeactivate}
      bubbleToolbar={bubbleToolbar}
      className={cn('editable-text', className)}
      style={style}
    />
  )
}
